import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  type ComboboxProps,
} from "@headlessui/react";
import { TournamentCompetition } from "@mixitone/models";
import ApplicationController, { ApplicationView } from "@mixitone/mvc";
import clsx from "clsx";

interface Props {
  autoFocus?: boolean;
  onSelect(competition: TournamentCompetition): void;
  competitions: TournamentCompetition[];
  value: TournamentCompetition | null;
  error?: string;
}

interface State {
  competitions: TournamentCompetition[];
  filteredCompetitions: TournamentCompetition[];
  search: string;
  onSelect(competition: TournamentCompetition): void;
}

class CompetitionSearchController extends ApplicationController<State, Props> {
  get initialState(): State {
    return {
      competitions: [],
      filteredCompetitions: [],
      search: "",
      onSelect: () => {},
    };
  }

  async initialize(props: Props) {
    this.changeProps(props);
  }

  async changeProps(props: Props) {
    if (!props.competitions) throw new Error("competitions is required");
    this.state.competitions = props.competitions;
    this.state.onSelect = props.onSelect;
  }

  actionSetSearch(search: string) {
    this.state.search = search;

    if (search.length < 2) {
      this.state.filteredCompetitions = [];
      return;
    }

    this.state.filteredCompetitions = this.state.competitions.filter((competition) =>
      (competition.name || "").toLowerCase().includes(search.toLowerCase()),
    );
  }

  actionSelect(competition: TournamentCompetition) {
    this.state.onSelect(competition);
    this.actionSetSearch("");
  }
}

const TournamentCompetitionSearch: React.FC<Props> = ({ value, autoFocus, error }) => {
  const controller = CompetitionSearchController.use();
  const { filteredCompetitions } = controller.state;

  const options = filteredCompetitions.map((competition, idx) => (
    <ComboboxOption
      key={competition.id}
      value={competition}
      // biome-ignore lint/a11y/useValidAriaValues: old
      aria-roledescription=""
      className={clsx(
        "border-l border-r border-slate-300 p-4 ui-active:bg-brand-100 ui-active:text-black ui-not-active:bg-white ui-not-active:text-black",
        { "rounded-b-lg border-b": idx === filteredCompetitions.length - 1 },
      )}
    >
      <div className="flex items-center justify-between">
        <span>{competition.name}</span>
      </div>
    </ComboboxOption>
  ));

  // Trick to make the combobox accept the union type
  const comboboxProps: ComboboxProps<typeof value, false> = {
    value: value,
  };

  return (
    <Combobox
      {...comboboxProps}
      onChange={(v) => {
        if (v instanceof TournamentCompetition) {
          controller.actionSelect(v);
        }
      }}
    >
      <div className="relative w-full">
        <ComboboxInput
          title="Search for a competition"
          data-testid="competition-search-input"
          data-1p-ignore
          autoFocus={autoFocus}
          autoComplete="off"
          autoCorrect="off"
          placeholder="Search for competition"
          className={clsx(
            "mt-1 block w-full rounded border p-2",
            error ? "border-red-500" : "border-gray-300",
          )}
          onChange={(event) => controller.actionSetSearch(event.target.value)}
          displayValue={(_value: TournamentCompetition) => ""}
        />
        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center pl-3">
          <svg
            aria-hidden="true"
            className="h-5 w-5 text-gray-500 dark:text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>
      {error && <div className="mt-1 text-sm text-red-500">{error}</div>}
      <ComboboxOptions
        anchor="bottom start"
        className="relative z-50"
        style={{ width: "var(--input-width)" }}
      >
        {options}
      </ComboboxOptions>
    </Combobox>
  );
};

const CompetitionSearchScoped = CompetitionSearchController.scope(
  ApplicationView(TournamentCompetitionSearch),
);
export { CompetitionSearchScoped as TournamentCompetitionSearch };
